import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import ButtonTsoPq from '@components/Buttons/ButtonTsoPq';
import SegmentedButtonTsoPq from '@components/Buttons/SegmentedButtonTsoPq';
import ChipSelectorTsoPq from '@components/ChipSelector/ChipSelectorTsoPq';
import DatepickerTsoPq from '@components/Datepicker/DatepickerTsoPq';
import SelectTsoPq from '@components/Select/SelectTsoPq';
import TreeAutocompleteTsoPq from '@components/TreeAutocomplete/TreeAutocompleteTsoPq';
import { parseSubstationDevices, TreeListItemTsoPqProps } from '@custom-types/TreeTypes';
import { Box, styled, Typography } from '@mui/material';
import { StyledSectionWrapper } from '@pages/MeasurmentExport/MeasurmentExportStyles';
import { useGetAggregationIntervalGetAllQuery } from '@redux/apiSlices/aggregationIntervalApi';
import { useGetMeasurementDeviceGetAllQuery } from '@redux/apiSlices/measurementDeviceApi';
import {
  ApiCommonExternalDtoMeasurementParameterExternalDto,
  useGetMeasurementParameterGetAllQuery,
} from '@redux/apiSlices/measurementParameterApi';
import { useLazyGetSubstationGetSubstationDeviceHierarchyQuery } from '@redux/apiSlices/substationApi';
import { useGetTsoGetAllQuery } from '@redux/apiSlices/tsoApi';
import { useAppSelector } from '@redux/hooks';
import {
  insertActiveTableRow,
  measurementVisualizationActiveTableRowProps,
  measurementVisualizationTableRowProps,
  setEditingRow,
  setHasUnsavedChanges,
  setNewRow,
  setUnsavedChanges,
  upsertTableRow,
} from '@redux/slices/measurementVisualizationSlice';
import { setSnackbar } from '@redux/slices/snackbarSlice';
import { useFlattenTreeProps } from '@utils/hooks/useFlattenTreeProp';
import { useLtreeTreeListFromObjects } from '@utils/hooks/useTransfromLtreeInHyerarchy';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { findDiff } from '../utils';

export type DeviceProps = { id: string; entityId: number; label: 'string'; technicalName: string; type: string };

export type TsosStateProps = {
  id: number;
  name: string;
  active: boolean;
  abbreviation: string;
};

export type Option = {
  id: number;
  value?: string;
  label: string;
};

type AggregationIntervalProps = {
  id: number;
  label: string;
  value: string;
};

export type RowDataProps = {
  entityId: number;
  id: string;
  usedParameters: any[];
  measurementDevice: string;
  measurementDeviceTechnicalName: string;
  tsoId: number;
  aggregationInterval: string;
  aggregationIntervalType: string;
  aggregationIntervalId: string;
  start: string;
  uniqueId?: number;
  end: string;
};

export const StyledWrapper = styled(Box)(() => {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_4,
  };
});

export const StyledLineWrapper = styled(Box)(() => {
  return {
    display: 'flex',
    gap: tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_page_gap_xs,
  };
});

const StyledSegmentedButtonTsoPq = styled(SegmentedButtonTsoPq)(() => {
  return {
    display: 'flex',
  };
});

export const StyledMeasurementDeviceTitle = styled(Typography)(() => {
  return {
    color: tsoPqTheme.palette.customTextColor.color_font_ft_text,
    fontFamily: tsoPqTheme.typography.openSans,
    fontSize: tsoPqTheme.typography.fontsize_body_body_large,
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: tsoPqTheme.typography.lineHeight_xl,
  };
});

export const StyledButtonWrapper = styled(Box)(() => {
  return {
    paddingTop: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_12,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_page_gap_xxs,
  };
});

export const defaultTsos = [
  { name: 'Amprion', active: false, id: 2, abbreviation: '' },
  { name: 'Tennet', active: false, id: 3, abbreviation: '' },
  { name: '50Hertz', active: false, id: 1, abbreviation: '' },
  { name: 'TransnetBW', active: false, id: 4, abbreviation: '' },
];

const MeasurementVisualizationParameters = () => {
  // values
  const [activeTsos, setActiveTsos] = useState<TsosStateProps[] | []>([]);
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [aggregationInterval, setAggregationInterval] = useState<Option | null | undefined>(undefined);
  const [aggregationType, setAggregationType] = useState<string | undefined>(undefined);
  const [selectedParameters, setSelectedParameters] = useState<string[] | undefined>(undefined);
  const [selectedDevices, setSelectedDevices] = useState<{ [key: string]: string[] } | undefined>(undefined);
  const [resetTrigger, setResetTrigger] = useState<boolean>(false);
  const [activeTsoName, setActiveTsoName] = useState<string>('');
  const [filteredParameters, setFilteredParameters] = useState<ApiCommonExternalDtoMeasurementParameterExternalDto[]>(
    []
  );

  // options
  const [aggregationIntervalsOptions, setAggregationIntervalsOptions] = useState<AggregationIntervalProps[]>([]);
  const [parameterOptions, setParameterOptions] = useState<TreeListItemTsoPqProps[] | undefined>([]);
  const [tsoDeviceOptions, setTsoDeviceOptions] = useState<{
    [key: number]: TreeListItemTsoPqProps[] | undefined;
  }>({});

  const { data: tsos, isSuccess } = useGetTsoGetAllQuery();
  const { data: aggregationIntervals } = useGetAggregationIntervalGetAllQuery();
  const { data: measurementParameters } = useGetMeasurementParameterGetAllQuery();
  const { data: measurementDevices } = useGetMeasurementDeviceGetAllQuery();
  const editingRow = useAppSelector((state) => state.measurementVisualization.editingRow) ?? undefined;
  const tableRows = useAppSelector((state) => state.measurementVisualization.tableRows) ?? undefined;
  const activeTableRows = useAppSelector((state) => state.measurementVisualization.activeTableRows) ?? undefined;
  const newRow = useAppSelector((state) => state.measurementVisualization.newRow) ?? undefined;
  const [triggerGetDeviceHierarchy] = useLazyGetSubstationGetSubstationDeviceHierarchyQuery();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const deviceType = 2;
  const parameters = useLtreeTreeListFromObjects(filteredParameters);

  // constants

  const aggregationTypeOptions = [
    {
      segmentValue: 'c10m_c',
      segmentLabel: t('measurementVisualization.parameters.current'),
    },
    {
      segmentValue: 'c10m_v',
      segmentLabel: t('measurementVisualization.parameters.voltage'),
    },
  ];
  const aggregationIntervalTenMinutes = 'c10m';
  const prevActiveTsosRef = useRef<TsosStateProps[]>([]);

  const handleActiveTsosChange = (tsos: TsosStateProps[]) => {
    const lastActiveTsoName = tsos.at(-1)?.name ?? '';

    setSelectedDevices((prev) => {
      const tsosWithSelectedDevices = prev ? Object.keys(prev) : [];
      const selectedTsosIds = tsos.map((tso) => `${tso.id}`);
      const unselectedTsos = tsosWithSelectedDevices.filter((tso) => !selectedTsosIds.includes(tso));

      const previousSelectedDevices = prev;

      previousSelectedDevices &&
        unselectedTsos?.map((tso) => {
          if (previousSelectedDevices[tso]) {
            delete previousSelectedDevices[tso];
          }
        });

      return previousSelectedDevices;
    });

    setActiveTsos(tsos);
    setActiveTsoName(lastActiveTsoName);
  };

  const handleStartDateChange = (date: dayjs.Dayjs) => {
    setStartDate(dayjs(date).format('YYYY-MM-DD HH:mm'));
  };

  const handleEndDateChange = (date: dayjs.Dayjs) => {
    setEndDate(dayjs(date).format('YYYY-MM-DD HH:mm'));
  };

  const handleAggregationIntervalChange = (option: Option | null) => {
    setAggregationInterval(option);
    if (option?.value !== aggregationIntervalTenMinutes) {
      setAggregationType(option?.value);
    }

    if (option?.value === aggregationIntervalTenMinutes) {
      setAggregationType(undefined);
    }
  };

  const handleAggregationTypeChange = (options: string[]) => {
    if (options.length > 0) setAggregationType(options[0]);
  };

  const handleParametersChange = (selectedParameters: string[]) => {
    setSelectedParameters(selectedParameters);
  };

  const handleDevicesChange = (id: number, selectedDevices: string[]) => {
    if (id && selectedDevices.length > 0) {
      setSelectedDevices((prev) => ({
        ...prev,
        [id]: selectedDevices,
      }));
    }
    if (id && selectedDevices.length === 0 && !editingRow) {
      setSelectedDevices((prev) => ({
        ...prev,
        [id]: [],
      }));
    }
  };

  const resetValues = async () => {
    setResetTrigger(true);
    await setActiveTsos([]);
    await setStartDate(undefined);
    await setEndDate(undefined);
    await setAggregationInterval(null);
    await setAggregationType(undefined);
    await setSelectedParameters(undefined);
    await setSelectedDevices(undefined);
    await dispatch(setEditingRow(undefined));
    await dispatch(setNewRow(false));
    setResetTrigger(false);
  };

  const submitValues = () => {
    const nodesProperties = Object.values(tsoDeviceOptions).flatMap(
      (entry) => entry && useFlattenTreeProps(entry, ['id', 'entityId', 'label', 'technicalName', 'type'])
    ) as DeviceProps[];
    const deviceProperties = nodesProperties.filter((device) => Number(device?.type) === deviceType);
    const allDevices = measurementDevices?.externalDtos;

    const usedParametersOptions =
      parameterOptions &&
      (useFlattenTreeProps(parameterOptions, ['id', 'entityId', 'label', 'type']) as {
        id: string;
        entityId: number;
        label: string;
        type: string;
      }[]);
    const filteredusedParametersOptions = usedParametersOptions
      ?.filter((parameter) => parameter.type === 'item')
      .map((filteredParameter) => ({
        id: filteredParameter.entityId,
        label: filteredParameter.label,
        value: filteredParameter.id,
      }));

    const usedParameters = selectedParameters?.map((parameter) =>
      filteredusedParametersOptions?.find((option) => option.value === parameter)
    );

    const tableRows =
      selectedDevices &&
      (Object.entries(selectedDevices).flatMap(([tsoId, devices]) =>
        devices.map((deviceId) => {
          const matchedDevice = deviceProperties.find((device) => device.id === deviceId);
          return {
            uniqueId: editingRow ?? `${matchedDevice?.entityId}-${Date.now()}`,
            entityId: matchedDevice?.entityId ?? NaN,
            id: deviceId ?? '',
            usedParameters: usedParameters ?? [],
            measurementDevice: matchedDevice?.label ?? '',
            measurementDeviceTechnicalName:
              allDevices?.find((device) => device.id === matchedDevice?.entityId)?.technicalName ?? '',
            tsoId: tsoId,
            aggregationInterval: aggregationInterval?.label ?? '',
            aggregationIntervalType: aggregationType ?? '',
            aggregationIntervalId: aggregationInterval?.id ?? NaN,
            start: startDate ?? '',
            end: endDate ?? '',
          };
        })
      ) as measurementVisualizationTableRowProps[] | []);

    if (!editingRow && tableRows && tableRows?.length > 0) {
      tableRows.map((tableRow: measurementVisualizationTableRowProps) => dispatch(upsertTableRow(tableRow)));
      dispatch(setNewRow(false));
    }

    if (editingRow && tableRows && tableRows?.length > 0) {
      tableRows.map((tableRow: measurementVisualizationTableRowProps) => dispatch(upsertTableRow(tableRow)));
      dispatch(setEditingRow(undefined));
      const activeTableRow = activeTableRows.find((row) => row.uniqueId === editingRow);
      if (activeTableRow)
        tableRows
          .map((tableRow, index) => ({ ...tableRow, order: index }))
          .map((activeTableRow: measurementVisualizationActiveTableRowProps) =>
            dispatch(
              insertActiveTableRow({
                ...activeTableRow,
                parameters: activeTableRow.usedParameters.map((parameter) => parameter.value),
              })
            )
          );
    }
  };

  useEffect(() => {
    const unfilteredParameters = measurementParameters?.externalDtos;
    const intervals = aggregationIntervals?.externalDtos;
    const intervalId = intervals?.find((interval) => interval.technicalName === aggregationType)?.id;
    const filteredParameters = unfilteredParameters?.filter(
      (unfilteredParameter) => unfilteredParameter.aggregationIntervalId === intervalId
    );
    setFilteredParameters(filteredParameters ?? []);
  }, [measurementParameters, aggregationType]);

  useEffect(() => {
    setParameterOptions(parameters);
  }, [parameters]);

  useEffect(() => {
    const intervals = aggregationIntervals?.externalDtos;

    if (intervals) {
      const options =
        intervals
          .map((interval) => {
            if (interval.technicalName.includes(aggregationIntervalTenMinutes)) {
              return {
                id: interval.id!,
                label: t('measurementVisualization.parameters.aggregationinterval.10minutes'),
                value: aggregationIntervalTenMinutes,
              };
            } else {
              return {
                id: interval.id!,
                label: t(`measurementVisualization.parameters.${interval.name}`),
                value: interval.technicalName,
              };
            }
          })
          .filter((item, index, self) => index === self.findIndex((i) => i.label === item.label)) ?? [];
      setAggregationIntervalsOptions(options);
    }
  }, [aggregationIntervals]);

  useEffect(() => {
    const setInitialEditData = async ({ row }: { row: measurementVisualizationTableRowProps }) => {
      const matchedTso = tsos.externalDtos.filter(
        (tso: { id: string | number }) => Number(tso.id) === Number(row.tsoId)
      )[0];
      setActiveTsos([
        {
          id: Number(row.tsoId),
          name: matchedTso.name,
          active: true,
          abbreviation: matchedTso.abbreviation,
        },
      ]);
      setStartDate(row.start);
      setEndDate(row.end);
      setAggregationInterval({
        id: row.aggregationIntervalId,
        label: row.aggregationInterval,
        value: !row.aggregationIntervalType.includes(aggregationIntervalTenMinutes)
          ? row.aggregationIntervalType
          : aggregationIntervalTenMinutes,
      });
      setAggregationType(row.aggregationIntervalType);
      setSelectedParameters(row.usedParameters.map((parameter: { value: any }) => parameter.value));
      setSelectedDevices({ [row.tsoId]: [row.id] });
    };

    tableRows.length > 0 &&
      tableRows.filter((row) => {
        if (row.uniqueId === editingRow) {
          setInitialEditData({ row });
        }
      });
  }, [editingRow]);

  useEffect(() => {
    const nodesProperties = Object.values(tsoDeviceOptions).flatMap(
      (entry) => entry && useFlattenTreeProps(entry, ['id', 'entityId', 'label', 'type'])
    ) as DeviceProps[];
    const usedParametersOptions =
      parameterOptions &&
      (useFlattenTreeProps(parameterOptions, ['id', 'entityId', 'label', 'type']) as {
        id: string;
        entityId: number;
        label: string;
        type: string;
      }[]);
    const selectedDeviceId = selectedDevices && Object.values(selectedDevices)[0]?.at(-1);
    const curentDeviceProps = nodesProperties
      .filter((node) => Number(node.type) === Number(deviceType))
      .filter((device) => device.id === selectedDeviceId)[0];
    const rowSavedValues = tableRows.filter((row) => row.uniqueId === editingRow)[0];
    const filteredusedParametersOptions = usedParametersOptions
      ?.filter((parameter) => parameter.type === 'parameter')
      .map((filteredParameter) => ({
        id: filteredParameter.entityId,
        label: filteredParameter.label,
        value: filteredParameter.id,
      }));

    const currentEntityId = curentDeviceProps?.entityId;
    const currentDevice = curentDeviceProps?.label;
    const currentTechnicalName =
      measurementDevices?.externalDtos?.find((device) => device.id === currentEntityId)?.technicalName ?? '';
    const currentUsedParameters = selectedParameters?.map((parameter) =>
      filteredusedParametersOptions?.find((option: { value: string }) => option.value === parameter)
    );

    const currentValues = {
      ...rowSavedValues,
      entityId: currentEntityId,
      id: selectedDeviceId,
      usedParameters: currentUsedParameters,
      measurementDevice: currentDevice,
      measurementDeviceTechnicalName: currentTechnicalName,
      aggregationInterval: aggregationInterval?.label,
      aggregationIntervalType: aggregationType,
      aggregationIntervalId: aggregationInterval?.id,
      start: startDate,
      end: endDate,
    } as unknown as measurementVisualizationTableRowProps;

    const isEqual = JSON.stringify(currentValues) === JSON.stringify(rowSavedValues);

    if (editingRow && !isEqual) {
      dispatch(setUnsavedChanges(currentValues));
      dispatch(setHasUnsavedChanges(true));
    }
  }, [
    activeTsos,
    startDate,
    endDate,
    aggregationInterval,
    aggregationType,
    selectedParameters,
    selectedDevices,
    resetTrigger,
    activeTsoName,
  ]);

  useEffect(() => {
    if (activeTsos) {
      const diff = findDiff({ activeTsos: activeTsos, prevActiveTsosRef: prevActiveTsosRef });

      const fetchDevices = async (id: number) => {
        if (isSuccess && tsos?.externalDtos?.length > 0) {
          try {
            const result = await triggerGetDeviceHierarchy(id, true);
            const parsedResult = result.data?.externalDtos && parseSubstationDevices(result.data?.externalDtos);
            parsedResult && setTsoDeviceOptions((prev) => ({ ...prev, [id]: parsedResult }));
          } catch {
            dispatch(
              setSnackbar({
                open: true,
                type: 'error',
                textContent: 'Failed to fetch devices',
              })
            );
          }
        }
      };
      if (!Object.keys(tsoDeviceOptions).includes(`${diff[0]?.id}`)) {
        prevActiveTsosRef.current.length < activeTsos.length && fetchDevices(diff[0].id);
        newRow && fetchDevices(activeTsos[0].id);
      }

      prevActiveTsosRef.current = activeTsos;
    }
  }, [activeTsos, activeTsoName]);

  return (
    <StyledWrapper>
      <StyledSectionWrapper>
        <ChipSelectorTsoPq
          customWidth="30.5rem"
          label={t('measurementVisualization.parameters.tsoSelect')}
          chips={tsos?.externalDtos ?? defaultTsos}
          activeChips={activeTsos}
          disabled={!!editingRow && !newRow}
          handleChange={handleActiveTsosChange}
          multiple={!newRow}
        ></ChipSelectorTsoPq>
        <StyledLineWrapper>
          <DatepickerTsoPq
            label={t('measurementVisualization.parameters.start')}
            supportingText={''}
            type={'datetimepicker'}
            width="15rem"
            enableFutureDates={false}
            error={false}
            date={startDate}
            handleChange={handleStartDateChange}
          />
          <DatepickerTsoPq
            label={t('measurementVisualization.parameters.end')}
            supportingText={''}
            type="datetimepicker"
            width="15rem"
            enableFutureDates={false}
            error={false}
            handleChange={handleEndDateChange}
            date={endDate}
            minDateTime={dayjs(startDate)}
          />
        </StyledLineWrapper>
        <StyledLineWrapper>
          <SelectTsoPq
            label={t('measurementVisualization.parameters.aggregationInterval')}
            placeholder={t('measurementVisualization.parameters.aggregationInterval')}
            options={aggregationIntervalsOptions}
            customwidth="15rem"
            handleChange={handleAggregationIntervalChange}
            reset={resetTrigger}
            value={aggregationInterval}
          />
          <StyledSegmentedButtonTsoPq
            disabled={aggregationInterval?.value !== aggregationIntervalTenMinutes}
            customWidth="15rem"
            options={aggregationTypeOptions}
            sizeVariant="m"
            defaultValue={aggregationType}
            handleChange={handleAggregationTypeChange}
          />
        </StyledLineWrapper>
        <TreeAutocompleteTsoPq
          disabled={!aggregationType}
          customwidth="31.5rem"
          options={parameterOptions ?? []}
          label={t('measurementVisualization.parameters.measurmentParameters')}
          placeholder={t('general.error.valueSelect')}
          handleChange={handleParametersChange}
          defaultValue={selectedParameters}
        />
      </StyledSectionWrapper>
      {activeTsos?.map((tso) => (
        <StyledSectionWrapper key={tso.id} selectDevice>
          <StyledMeasurementDeviceTitle>{tso.name}</StyledMeasurementDeviceTitle>
          <TreeAutocompleteTsoPq
            customwidth="31.5rem"
            options={tsoDeviceOptions[tso.id] ?? []}
            label={t('measurementVisualization.parameters.measurementDevice')}
            placeholder={t('general.error.valueSelect')}
            lowestLevel={3}
            handleChange={(values) => handleDevicesChange(tso.id, values)}
            defaultValue={selectedDevices?.[tso.id as keyof typeof selectedDevices]}
            multiple={!editingRow}
          />
        </StyledSectionWrapper>
      ))}
      <StyledButtonWrapper>
        <ButtonTsoPq
          onClick={() => {
            resetValues();
          }}
          sizeVariant="m"
          buttonType="outlinedPrimary"
          textContent={t('measurementVisualization.form.discard')}
        ></ButtonTsoPq>
        <ButtonTsoPq
          onClick={() => {
            submitValues();
          }}
          sizeVariant="m"
          buttonType="filledPrimary"
          textContent={t('measurementVisualization.form.submit')}
        ></ButtonTsoPq>
      </StyledButtonWrapper>
    </StyledWrapper>
  );
};

export default MeasurementVisualizationParameters;
